import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AddressBook, CaretLeft, Plus, PencilSimple, Trash, UploadSimple } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const inputCls = "h-11 bg-transparent border-white/10 text-white placeholder:text-white/25 focus:border-white/40 focus-visible:ring-1 focus-visible:ring-white/40";

export default function MarketingListePage() {
  const [lists, setLists] = useState([]);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [emails, setEmails] = useState("");
  const [phones, setPhones] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [importingList, setImportingList] = useState(null);
  const csvInputRef = useRef(null);
  const csvTargetRef = useRef(null);

  const openCsvPicker = (list) => {
    csvTargetRef.current = list;
    csvInputRef.current?.click();
  };

  const handleCsvFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const list = csvTargetRef.current;
    if (!file || !list) return;
    setImportingList(list.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/marketing/lists/${list.id}/import-csv`, fd);
      toast.success(data.message, { duration: 9000 });
      loadLists();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail), { duration: 9000 });
    } finally {
      setImportingList(null);
    }
  };

  const loadLists = () => api.get("/admin/marketing/lists").then(({ data }) => setLists(data)).catch(() => {});
  useEffect(() => { loadLists(); }, []);

  const openNew = () => {
    setName("");
    setEmails("");
    setPhones("");
    setEditing({ id: null });
  };

  const openEdit = (l) => {
    setName(l.name);
    setEmails((l.emails || []).join(", "));
    setPhones((l.phones || []).join(", "));
    setEditing(l);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/marketing/lists", {
        id: editing?.id || null,
        name,
        emails: emails.split(/[,;\n]/).map((r) => r.trim()).filter(Boolean),
        phones: phones.split(/[,;\n]/).map((r) => r.trim()).filter(Boolean),
      });
      toast.success(editing?.id ? "Lista aggiornata" : "Lista creata");
      setEditing(null);
      loadLists();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const id = deleting?.id;
    setDeleting(null);
    try {
      await api.delete(`/admin/marketing/lists/${id}`);
      toast.success("Lista eliminata");
      loadLists();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="marketing-liste-page">
      <input ref={csvInputRef} type="file" accept=".csv,.txt" onChange={handleCsvFile} className="hidden" data-testid="csv-file-input" />
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8 animate-fade-up">
        <div>
          <Link to="/marketing/email" data-testid="back-to-email" className="inline-flex items-center gap-1 text-xs font-semibold text-white/50 hover:text-white transition-colors mb-4">
            <CaretLeft size={13} /> Email
          </Link>
          <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2 flex items-center gap-1.5">
            <AddressBook size={13} className="text-violet-400" /> Liste contatti centralizzate
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none">Liste</h1>
          <p className="text-sm text-slate-400 mt-3 max-w-2xl">
            Crea liste di contatti riutilizzabili come destinatari delle newsletter. Puoi anche importare contatti in blocco da un file CSV con il pulsante "CSV" su ogni lista.
          </p>
        </div>
        <Button data-testid="new-list-button" onClick={openNew}
          className="h-11 px-6 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
          <Plus size={16} className="mr-1.5" /> Nuova lista
        </Button>
      </div>

      {lists.length === 0 ? (
        <div className="p-10 rounded-lg bg-surface border border-surface-border text-center animate-fade-up" data-testid="lists-empty">
          <AddressBook size={40} weight="duotone" className="text-white/20 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Nessuna lista ancora. Crea la prima con "Nuova lista".</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="lists-grid">
          {lists.map((l, i) => (
            <div key={l.id} className="p-6 rounded-lg bg-surface border border-surface-border animate-fade-up" data-testid={`list-card-${l.id}`} style={{ animationDelay: `${i * 60}ms` }}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-heading text-xl font-semibold tracking-tight truncate" title={l.name}>{l.name}</h3>
                  <p className="text-xs text-slate-400 mt-1">
                    <span className="text-white font-semibold">{(l.emails || []).length}</span> contatti
                    {(l.phones || []).length > 0 && <> — <span className="text-emerald-400 font-semibold">{l.phones.length}</span> numeri WhatsApp</>}
                    {l.updated_at && <> — aggiornata {new Date(l.updated_at).toLocaleDateString("it-IT")}</>}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button data-testid={`list-import-csv-${l.id}`} onClick={() => openCsvPicker(l)} disabled={importingList === l.id} variant="ghost"
                    className="h-9 px-4 border border-emerald-400/30 text-emerald-300/80 hover:bg-emerald-400/10 hover:text-emerald-300 rounded-full text-xs font-semibold disabled:opacity-40">
                    <UploadSimple size={13} className="mr-1" /> {importingList === l.id ? "Import..." : "CSV"}
                  </Button>
                  <Button data-testid={`list-edit-${l.id}`} onClick={() => openEdit(l)} variant="ghost"
                    className="h-9 px-4 border border-white/15 text-white/70 hover:bg-white/5 hover:text-white rounded-full text-xs font-semibold">
                    <PencilSimple size={13} className="mr-1" /> Gestisci
                  </Button>
                  <Button data-testid={`list-delete-${l.id}`} onClick={() => setDeleting(l)} variant="ghost"
                    className="h-9 w-9 p-0 rounded-full text-red-400/60 hover:text-red-400 hover:bg-red-500/10">
                    <Trash size={15} />
                  </Button>
                </div>
              </div>
              <p className="text-xs text-white/40 mt-3 line-clamp-2 break-all">{(l.emails || []).slice(0, 6).join(", ")}{(l.emails || []).length > 6 ? "…" : ""}</p>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="bg-surface border-white/10 text-white sm:max-w-lg" data-testid="list-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-xl flex items-center gap-2">
              <AddressBook size={20} weight="duotone" className="text-violet-400" /> {editing?.id ? "Gestisci lista" : "Nuova lista"}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Nome della lista e indirizzi email dei contatti, separati da virgola o a capo.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Nome lista</Label>
              <Input required data-testid="list-name-input" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Clienti ristorante" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Contatti ({emails.split(/[,;\n]/).map((r) => r.trim()).filter(Boolean).length})</Label>
              <Textarea data-testid="list-emails-input" value={emails} onChange={(e) => setEmails(e.target.value)} rows={5}
                placeholder="a@x.it, b@x.it&#10;c@x.it"
                className="bg-transparent border-white/10 text-white placeholder:text-white/25 focus:border-white/40 focus-visible:ring-1 focus-visible:ring-white/40 resize-y" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Numeri WhatsApp (opzionale, formato +39...)</Label>
              <Textarea data-testid="list-phones-input" value={phones} onChange={(e) => setPhones(e.target.value)} rows={2}
                placeholder="+393331234567, +393407654321"
                className="bg-transparent border-white/10 text-white placeholder:text-white/25 focus:border-white/40 focus-visible:ring-1 focus-visible:ring-white/40 resize-y" />
            </div>
            <Button type="submit" disabled={saving} data-testid="list-save-button"
              className="h-11 px-6 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
              {saving ? "Salvataggio..." : "Salva lista"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent className="bg-surface border-white/10 text-white" data-testid="list-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Eliminare la lista "{deleting?.name}"?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              La lista con {(deleting?.emails || []).length} contatti verrà eliminata definitivamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="list-delete-confirm" onClick={handleDelete} className="bg-red-500 text-white hover:bg-red-600 rounded-full font-semibold">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
